import { useState } from "react";
import { optionsForMarket, type SingleQuestionDefinition } from "@ferrivia/api-contract";
import { useTracker } from "@ferrivia/auth-web";
import { useMarket } from "@ferrivia/i18n";
import type { OptionGuide } from "@ferrivia/questionnaire-ui";
import { Sheet, SheetContent, SheetTitle, Text } from "@ferrivia/ui";
import { useDismissOnBack } from "../../hooks/useDismissOnBack";
import s from "./OptionGuideSheet.module.css";

// Repeated rather than threaded down, for the reason SupplementPicker gives.
const INSTRUMENT = "health_questionnaire" as const;

export interface OptionGuideSheetProps {
  question: SingleQuestionDefinition;
  guide: OptionGuide;
  optionLabelFor: (optionId: string) => string;
  /** The section screen's own fixed layer: a sheet portalled into `#root`
      would open underneath the questionnaire. */
  container: HTMLElement | null;
}

/** A link under a choice question that opens a sheet describing each of its
    options, in the question's own option order. */
export function OptionGuideSheet({ question, guide, optionLabelFor, container }: OptionGuideSheetProps) {
  const tracker = useTracker();
  const market = useMarket();
  const [open, setOpen] = useState(false);

  useDismissOnBack(open, () => setOpen(false), `optionGuide:${question.key}`);

  const described = optionsForMarket(question.options ?? [], market).filter((option) => guide.options[option.id]);

  return (
    <>
      <button
        type="button"
        className={s.trigger}
        onClick={() => {
          tracker.track("questionnaire.option_guide_opened", { instrument: INSTRUMENT, question_key: question.key });
          setOpen(true);
        }}
      >
        {guide.linkText}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent container={container} aria-describedby={undefined}>
          <SheetTitle>{guide.title}</SheetTitle>
          <Text as="p" size="sm" className={s.intro}>
            {guide.intro}
          </Text>
          <dl className={s.list}>
            {described.map((option) => (
              <div key={option.id} className={s.item}>
                <dt className={s.term}>{optionLabelFor(option.id)}</dt>
                <dd className={s.description}>{guide.options[option.id]}</dd>
              </div>
            ))}
          </dl>
        </SheetContent>
      </Sheet>
    </>
  );
}
